/**
 * Top-level container for the Single-Row Details View.
 *
 * Flex column: bounded row-details panel on top + scrollable related-data
 * panel below. Fires `feature:single_row_view_open` analytics on mount with
 * column + inbound-relation counts (no PII).
 *
 * Slice 1 wires entry from EP2 (row context menu). Slice 3 adds the
 * breadcrumb chip-row above and the other entry points.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../../../components/ui/button';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { useSplitterDrag } from '../../../../layout/hooks/useSplitterDrag';
import { RowDetailsPanel } from './RowDetailsPanel';
import { RelatedDataPanel } from './RelatedDataPanel';
import { useInboundRelations } from '../hooks/useInboundRelations';
import type { SingleRowNodeRef } from '../types/singleRowTypes';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import type { TableExplorerInboundForeignKey } from '../../../../types/tableExplorer';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { ForeignKeyMap } from '../../table-explorer/hooks/useTableMetadata';

export type SingleRowViewProps = {
  readonly connectionId: string;
  readonly database: string;
  readonly engine: DatabaseEngineId;
  readonly nodeRef: SingleRowNodeRef;
  readonly row: Record<string, unknown>;
  readonly columns: ReadonlyArray<TableExplorerColumn>;
  readonly inboundForeignKeys: ReadonlyArray<TableExplorerInboundForeignKey>;
  readonly onClose: () => void;
  /**
   * Slice 3 (EP4) — FK cell click handler propagated to RowCell. The parent
   * TableExplorerTab pushes a row-node onto the navigation tree (or opens a
   * new TE tab on ⌘-click).
   */
  readonly onFkClick?: (args: { column: string; value: unknown; modifierKey: boolean }) => void;
  /**
   * Slice 3 (EP5/EP6) — related-data handlers propagated to RelatedDataPanel.
   * `onRelatedSectionOpen` pushes a filtered table-node; `onRelatedRowOpen`
   * pushes both table+row nodes (EP6 mini-grid drill).
   */
  readonly onRelatedSectionOpen?: (args: {
    relation: import('../types/singleRowTypes').InboundRelation;
  }) => void;
  readonly onRelatedRowOpen?: (args: {
    relation: import('../types/singleRowTypes').InboundRelation;
    row: Record<string, unknown>;
    via: 'icon' | 'context-menu';
    modifierKey: boolean;
  }) => void;
  /** When true, each cell in RowDetailsPanel renders an Edit (Pencil) affordance. */
  readonly editable?: boolean;
  /** Real-FK lookup keyed by column name (normalized). Required when editable for the FK picker. */
  readonly foreignKeyMap?: ForeignKeyMap;
  /** Called when the user commits a cell edit in the row-details panel. */
  readonly onEditCommit?: (args: { column: string; oldValue: unknown; newValue: unknown }) => void;
};

function hashPk(pk: SingleRowNodeRef['pk']): string {
  return pk.map((p) => `${p.column}=${JSON.stringify(p.value)}`).join('&');
}

export function SingleRowView({
  connectionId,
  database,
  engine,
  nodeRef,
  row,
  columns,
  inboundForeignKeys,
  onClose,
  onFkClick,
  onRelatedSectionOpen,
  onRelatedRowOpen,
  editable = false,
  foreignKeyMap,
  onEditCommit,
}: SingleRowViewProps) {
  const { track } = useAnalytics();
  const relations = useInboundRelations({ inboundForeignKeys });
  const pkHash = useMemo(() => hashPk(nodeRef.pk), [nodeRef.pk]);

  // Fire view-open analytics exactly once per (table, pk) visit.
  const trackedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${nodeRef.schema}.${nodeRef.table}#${pkHash}`;
    if (trackedRef.current === key) return;
    trackedRef.current = key;
    track('feature:single_row_view_open', {
      engine,
      columnCount: columns.length,
      inboundRelationCount: relations.length,
    });
  }, [engine, columns.length, relations.length, nodeRef.schema, nodeRef.table, pkHash, track]);

  // Resize state for the Related Data panel. `null` = dynamic (content-sized
  // with a 60% cap). Once the user drags the divider we switch to an explicit
  // pixel height — and reset to dynamic when the user navigates to a different
  // row (different pk-hash).
  const splitRef = useRef<HTMLDivElement>(null);
  const [relatedHeightPx, setRelatedHeightPx] = useState<number | null>(null);
  const dragStartClientYRef = useRef(0);
  const dividerHeight = 6;
  const relatedMinHeight = 36; // header-only fallback
  const rowDetailsMinHeight = 120;
  useEffect(() => {
    setRelatedHeightPx(null);
  }, [pkHash, nodeRef.schema, nodeRef.table]);

  /** Current rendered height of the related-data panel, whether or not the
   *  user has ever dragged (dynamic/content-sized state has no `relatedHeightPx`
   *  to anchor keyboard stepping to, so keyboard resize measures the DOM
   *  directly the same way a drag would land on it). */
  const measuredRelatedHeight = useCallback((): number => {
    if (relatedHeightPx !== null) return relatedHeightPx;
    const measured = splitRef.current
      ?.querySelector<HTMLElement>('[data-testid="related-data-panel"]')
      ?.getBoundingClientRect().height;
    return measured ?? relatedMinHeight;
  }, [relatedHeightPx]);

  const clampRelatedHeight = useCallback((value: number): number => {
    const rect = splitRef.current?.getBoundingClientRect();
    const max = rect ? Math.max(relatedMinHeight, rect.height - rowDetailsMinHeight - dividerHeight) : value;
    return Math.max(relatedMinHeight, Math.min(max, value));
  }, []);

  // CH-13 (T5.8c): pointer-capture + rAF drag, keyboard step, double-click
  // reset via the shared `useSplitterDrag` hook. Domain math (height
  // measured from the container's bottom edge to the pointer) is unchanged
  // from the pre-existing mousemove handler.
  const divider = useSplitterDrag({
    axis: 'y',
    onDrag: (deltaPx) => {
      const rect = splitRef.current?.getBoundingClientRect();
      if (!rect) return;
      const clientY = dragStartClientYRef.current + deltaPx;
      const fromBottom = rect.bottom - clientY - dividerHeight / 2;
      setRelatedHeightPx(Math.round(clampRelatedHeight(fromBottom)));
    },
    // Double-click resets to dynamic content sizing — this was already the
    // pre-existing (and correct) reset target, just not wired to analytics.
    onReset: () => {
      setRelatedHeightPx(null);
      track('feature:splitter_reset', { surface: 'single-row-related-data' });
    },
    onKeyStep: (direction, big) => {
      const step = big ? 32 : 8;
      setRelatedHeightPx(Math.round(clampRelatedHeight(measuredRelatedHeight() + direction * step)));
    },
  });

  const handleResizeDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      dragStartClientYRef.current = e.clientY;
      divider.onPointerDown(e);
    },
    [divider]
  );

  // Read-only measurement for `aria-valuemax` — best-effort, same as the
  // container-height reads `clampRelatedHeight`/`measuredRelatedHeight`
  // already do at drag/keyboard time; null before first mount.
  const splitRect = splitRef.current?.getBoundingClientRect();
  const maxRelatedHeight = splitRect
    ? Math.round(Math.max(relatedMinHeight, splitRect.height - rowDetailsMinHeight - dividerHeight))
    : undefined;

  return (
    <div
      className="h-full flex flex-col overflow-hidden"
      data-testid="single-row-view"
      data-schema={nodeRef.schema}
      data-table={nodeRef.table}
    >
      <header
        className="flex-shrink-0 flex items-center gap-2 px-2 py-1.5 border-b border-border bg-muted/40 select-none"
        data-testid="single-row-view-header"
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={onClose}
          data-testid="single-row-back-to-grid"
        >
          {/*
            Inline SVG (not <ChevronLeft /> from lucide-react). The icon
            itself was never broken — earlier debugging eliminated every
            React/CSS hypothesis (path data fine, size class applied, stroke
            non-empty). The "empty rectangle" was actually the
            TableExplorerGrid's sticky <th> elements (zIndex: 25-30) poking
            THROUGH the Single-Row overlay (z-10) and covering the icon
            area. That stacking-order bug is fixed in TableExplorerTab.tsx
            by bumping the overlay's z-index. We keep an inline SVG here as
            a small belt-and-braces measure so this surface no longer has
            any class-merge / Tailwind-content-scan / lucide-build coupling.
            The <path> data is the lucide ChevronLeft glyph
            ("m15 18-6-6 6-6"). Explicit width/height attrs guarantee a
            16x16 box even if no CSS rule applies.
          */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-1 shrink-0"
            aria-hidden="true"
            data-testid="single-row-back-to-grid-icon"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          Back to grid
        </Button>
        <div className="text-xs text-muted-foreground truncate select-text">
          {nodeRef.schema ? `${nodeRef.schema}.` : ''}
          {nodeRef.table}
          <span className="mx-1 text-muted-foreground/60">·</span>
          <span className="text-foreground">
            {nodeRef.pk.map((p) => `${p.column}=${String(p.value)}`).join(', ')}
          </span>
        </div>
      </header>
      <div ref={splitRef} className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <RowDetailsPanel
          schema={nodeRef.schema}
          table={nodeRef.table}
          pkHash={pkHash}
          row={row}
          columns={columns}
          onFkClick={onFkClick}
          editable={editable}
          connectionId={connectionId}
          database={database}
          engine={engine}
          foreignKeyMap={foreignKeyMap}
          onEditCommit={onEditCommit}
        />
        {/* CH-13: 1px `--divider` hairline + 6px invisible hit area,
            hover/drag pinned to the identical `--primary` tint, pointer-
            capture + rAF drag, keyboard resize, `role="separator"`. */}
        <div
          className="flex-shrink-0 h-1.5 cursor-row-resize flex items-center justify-center relative border-t border-border bg-transparent before:absolute before:inset-x-0 before:top-1/2 before:-translate-y-1/2 before:h-px before:bg-divider before:transition-colors before:content-[''] hover:before:bg-primary/60 data-[dragging=true]:before:bg-primary/60 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
          onPointerDown={handleResizeDown}
          onDoubleClick={divider.onDoubleClick}
          onKeyDown={divider.onKeyDown}
          style={{ touchAction: 'none', userSelect: 'none' }}
          data-testid="single-row-related-data-divider"
          aria-label="Resize related data panel (double-click to reset)"
          title="Drag to resize · double-click to reset"
          role="separator"
          aria-orientation="horizontal"
          aria-valuenow={relatedHeightPx !== null ? Math.round(relatedHeightPx) : undefined}
          aria-valuemin={relatedMinHeight}
          aria-valuemax={maxRelatedHeight}
          aria-valuetext={
            relatedHeightPx !== null ? `${Math.round(relatedHeightPx)} pixels` : 'Auto (sized to content)'
          }
          tabIndex={0}
          data-dragging={divider.isDragging ? 'true' : undefined}
        >
          <div className="w-8 h-1 bg-border rounded-full" />
        </div>
        <RelatedDataPanel
          connectionId={connectionId}
          database={database}
          engine={engine}
          schema={nodeRef.schema}
          table={nodeRef.table}
          pkHash={pkHash}
          relations={relations}
          parentRow={row}
          heightPx={relatedHeightPx}
          onRelatedSectionOpen={onRelatedSectionOpen}
          onRelatedRowOpen={onRelatedRowOpen}
        />
      </div>
    </div>
  );
}
